import { isCreditKind } from '@platform/domain';
import { useNavigate } from '@tanstack/react-router';
import { Copy, FileDown, PencilLine, Send, Trash2 } from 'lucide-react';
import type { DocumentStatus, InvoiceKind, Readiness } from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { fillTemplate } from '../../lib/fillTemplate';
import { Button, type ButtonSize } from '../../ui/Button';
import { commandBarStyles } from '../../ui/CommandBar';
import { cx } from '../../ui/cx';
import type { useDocumentActions } from './actions';
import styles from './Sales.module.css';

/** What the tools need to know about a document (a list row or the detail page). */
export interface ToolDocument {
  id: string;
  kind: string;
  status: DocumentStatus;
  number: string | null;
  version: number;
  readiness: Readiness | null;
  receiverName: string | null;
  cash: boolean;
  totalAmount: string | null;
}

export type DocumentToolsActions = ReturnType<typeof useDocumentActions>;

/**
 * The tools of one document by state and permission (D-2 v2), always in the order edit · issue and
 * send · copy into an invoice · PDF · delete. Shared by the list's command bar (test IDs `row-…`)
 * and the detail toolbar (`detail-…`); only these buttons carry test IDs.
 * - Draft: every tool; "issue" on an incomplete draft opens the editor, where the issues are listed.
 * - Any other state: copy and PDF. A viewer: PDF only.
 */
export function DocumentTools({
  orgId,
  doc,
  prefix,
  actions,
  size = 'md',
  className,
}: {
  orgId: string;
  doc: ToolDocument;
  prefix: 'row' | 'detail';
  actions: DocumentToolsActions;
  size?: ButtonSize;
  className?: string;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const access = useAccess(orgId);
  const isDraft = doc.status === 'draft';
  // RI-04: while a confirmed delete of this document waits out its undo window, its tools wait too,
  // so an edit, an issue or a copy cannot race the deferred discard. Undo gives them back.
  const isPending = actions.pendingDeleteId === doc.id;
  const target = {
    id: doc.id,
    number: doc.number,
    version: doc.version,
    kind: doc.kind as InvoiceKind,
    receiverName: doc.receiverName,
    cash: doc.cash,
    totalAmount: doc.totalAmount,
  };
  const openEditor = () =>
    void navigate({
      to: '/orgs/$orgId/sales/invoices/$docId/edit',
      params: { orgId, docId: doc.id },
    });
  const tool = (name: string) => `${prefix}-${name}`;
  return (
    <>
      {isDraft && access.can('invoice.edit') ? (
        <Button
          size={size}
          variant="secondary"
          className={className}
          isDisabled={isPending}
          icon={<PencilLine size={16} aria-hidden="true" />}
          testId={tool('edit')}
          onPress={openEditor}
        >
          {t.sales.edit}
        </Button>
      ) : null}
      {/* D-22 per D-5: issuing a credit note (`c`/`ec`) needs `invoice.cancel` as well, so a user
          without it (the sales role) sees NO "issue and send" action here — HIDDEN, never a disabled
          or 403 control. The server refuses it too, as defence in depth. Debit notes and invoices
          are unaffected. The user may still draft and edit the credit note. */}
      {isDraft &&
      access.can('invoice.issue') &&
      access.can('invoice.submit') &&
      (!isCreditKind(doc.kind) || access.can('invoice.cancel')) ? (
        <Button
          size={size}
          // The detail page's main action; in the list the start buttons hold the primary look.
          variant={prefix === 'detail' ? 'primary' : 'secondary'}
          className={className}
          isDisabled={isPending}
          icon={<Send size={16} aria-hidden="true" />}
          testId={tool('issue')}
          onPress={() => {
            // An incomplete draft opens the editor, where readiness lists what to fix.
            if (doc.readiness !== 'ready') openEditor();
            else actions.requestIssue(target);
          }}
        >
          {t.sales.issueSend}
        </Button>
      ) : null}
      {access.can('invoice.create') && (doc.kind === 'i' || doc.kind === 'ei') ? (
        <Button
          size={size}
          variant="secondary"
          className={className}
          isDisabled={isPending}
          icon={<Copy size={16} aria-hidden="true" />}
          testId={tool('copy')}
          onPress={() => actions.copy(doc.id)}
        >
          {t.sales.copyAs} {t.sales.copyInvoice}
        </Button>
      ) : null}
      <Button
        size={size}
        variant="secondary"
        className={className}
        isDisabled={isPending}
        icon={<FileDown size={16} aria-hidden="true" />}
        testId={tool('pdf')}
        onPress={() => actions.pdf({ id: doc.id, number: doc.number })}
      >
        {t.sales.downloadPdf}
      </Button>
      {isDraft && access.can('invoice.edit') ? (
        <Button
          size={size}
          variant="danger"
          className={className}
          isDisabled={isPending}
          icon={<Trash2 size={16} aria-hidden="true" />}
          testId={tool('delete')}
          onPress={() => actions.requestDelete(target)}
        >
          {t.sales.delete}
        </Button>
      ) : null}
    </>
  );
}

/**
 * The command bar's selection zone (BLOCK-1-REDESIGN §A.1): the selected document named by its
 * number and buyer, its tools (`doc-tools`, only the tool buttons inside) and «مسح الاختيار».
 * Never a count with a clear "x", never a menu (BR-1, AC-L2, AC-L3).
 */
export function SelectedDocumentZone({
  orgId,
  doc,
  actions,
  onClear,
}: {
  orgId: string;
  doc: ToolDocument;
  actions: DocumentToolsActions;
  onClear: () => void;
}) {
  const { t } = useI18n();
  return (
    <>
      <span className={cx(styles.selectedDoc, commandBarStyles.selectionLabel)}>
        {fillTemplate(
          t.sales.selectedDoc,
          <span className={styles.mono}>{doc.number ?? '—'}</span>,
        )}
        {' · '}
        {doc.cash ? t.sales.cash : <bdi>{doc.receiverName ?? t.sales.noCustomer}</bdi>}
      </span>
      <div className={commandBarStyles.selectionTools}>
        <div
          role="group"
          aria-label={t.sales.columns.actions}
          className={styles.docTools}
          data-testid="doc-tools"
        >
          <DocumentTools orgId={orgId} doc={doc} prefix="row" actions={actions} size="sm" />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={styles.textButton}
          testId="selection-clear"
          onPress={onClear}
        >
          {t.common.clearSelection}
        </Button>
      </div>
    </>
  );
}
